
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Target, BookOpen, Trophy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AIAnalysisResult } from "@/hooks/useAIAnalysis";

interface ActionPlanCardProps {
  actionPlan: AIAnalysisResult['actionPlan'];
}

const ActionPlanCard = ({ actionPlan }: ActionPlanCardProps) => {
  const { t } = useTranslation('ai-analysis');
  if (!actionPlan) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5 text-golf-600" />
          {t('actionPlan.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h4 className="font-semibold mb-2">{t('actionPlan.trainingPriorities')}</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant="destructive">1</Badge>
              <span className="text-sm">{actionPlan.priority1}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="default">2</Badge>
              <span className="text-sm">{actionPlan.priority2}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">3</Badge>
              <span className="text-sm">{actionPlan.priority3}</span>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              {t('actionPlan.weeklyGoals')}
            </h4>
            <ul className="space-y-1">
              {actionPlan.weeklyGoals.map((goal, index) => (
                <li key={index} className="text-sm text-gray-600 pl-2 border-l-2 border-golf-200">
                  {goal}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <Trophy className="h-4 w-4" />
              {t('actionPlan.monthlyGoals')}
            </h4>
            <ul className="space-y-1">
              {actionPlan.monthlyGoals.map((goal, index) => (
                <li key={index} className="text-sm text-gray-600 pl-2 border-l-2 border-golf-200">
                  {goal}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ActionPlanCard;
